import { createContext, useContext, Children } from 'react';
import { Link } from 'wouter';

/**
 * Shared deliverable-card data. Single source for the 6 real deliverables,
 * consumed by both the students-page DeliverablesGallery (3+3 grid) and the
 * landing page's TheClimb section (cards as stations on the climb). Each card
 * carries a SPECIMEN preview of the real artifact and a link to its public
 * sample. `phase` maps the card onto the 0-to-1 arc for the climb layout.
 */
export interface DeliverableData {
  tag: string;
  title: string;
  description: string;
  cta: string;
  href: string;
  preview: React.ReactNode;
  phase: 'spark' | 'invention' | 'product' | 'business';
  // The decision-funnel "filter line" shown on the compact climb card only:
  // the question this gate answers (mono, brass) + the expensive mistake it
  // spares you if the idea fails here (sans, muted). Optional — the full
  // (non-compact) gallery card ignores it.
  filter?: { question: string; consequence: string };
}

// Array order is the students-gallery 3+3 grid order (row 1 = the three
// advisory-board specialists, row 2 = operational deliverables). TheClimb
// filters by `phase` and preserves relative order within each phase, so this
// order also yields the right per-phase card order on the climb.
export const DELIVERABLES: DeliverableData[] = [
  {
    tag: 'Product Strategist',
    title: 'A needs discovery memo',
    description:
      'Market sizing, customer pain articulation, and a competitive landscape map. The first specialist conversation, before any patent work.',
    cta: 'See the example memo →',
    href: '/demo/sample-needs-discovery',
    preview: <NeedsDiscoveryMiniPreview />,
    phase: 'spark',
    filter: { question: 'Is there a real need?', consequence: 'Before any patent work.' },
  },
  {
    tag: 'IP Consultant',
    title: 'An invention strategy memo',
    description:
      'A first-meeting writeup with a Go / Pivot / Stop verdict, real US prior-art citations, and a recommended filing path.',
    cta: 'Read the example memo →',
    href: '/demo/memo',
    preview: <MemoMiniPreview />,
    phase: 'invention',
    filter: { question: 'Is it patentable?', consequence: 'Before any filing fees.' },
  },
  {
    tag: 'Financial Analyst',
    title: 'A business case memo',
    description:
      'Scenario-based 5-year revenue, cost, and EBITDA projections. Unit economics, breakeven, and a preliminary valuation range.',
    cta: 'See the example memo →',
    href: '/demo/sample-business-case',
    preview: <BusinessCaseMiniPreview />,
    phase: 'business',
    filter: { question: 'Do the numbers work?', consequence: 'Before you raise money.' },
  },
  {
    tag: 'Stage 03 · Search Strategy',
    title: 'A prior-art search plan',
    description:
      'The methodology a senior patent searcher builds before running queries: CPC classifications, keyword sets, and Boolean queries.',
    cta: 'See the example strategy →',
    href: '/demo/sample-search-strategy',
    preview: <SearchStrategyMiniPreview />,
    phase: 'invention',
    filter: { question: 'Has someone patented it?', consequence: 'Before you file.' },
  },
  {
    tag: 'Design Workspace',
    title: 'A buildable design breakdown',
    description:
      'Your idea broken into its real components — each with a function, materials, and how it connects. The foundation for prototyping and patent drawings.',
    cta: 'See the example design →',
    href: '/demo/sample-design',
    preview: <DesignMiniPreview />,
    phase: 'product',
    filter: { question: 'Can it actually be built?', consequence: 'Before prototyping spend.' },
  },
  {
    tag: 'Stage 06 · Marketplace',
    title: 'A vetted attorney match',
    description:
      'Over 50,000 USPTO-registered patent attorneys and agents — enriched with practice-area research and accessibility signals.',
    cta: 'Browse the marketplace →',
    href: '/marketplace',
    preview: <MarketplaceMiniPreview />,
    phase: 'business',
    filter: { question: 'Every filter cleared.', consequence: "Here's who files it." },
  },
];

/**
 * The Business Model Canvas card — a built feature with a public sample, but
 * NOT part of the curated six in the students-gallery narrative. It is added to
 * the climb's business phase only (before the attorney match). Kept out of
 * DELIVERABLES so the "Six structured deliverables" students gallery stays
 * exactly six; the climb uses CLIMB_DELIVERABLES below.
 */
export const BUSINESS_MODEL_CANVAS_CARD: DeliverableData = {
  tag: 'Business Planning',
  title: 'A business model canvas',
  description:
    'Your whole venture on one page: the nine building blocks of how it creates, delivers, and captures value, synthesized from your strategy memos.',
  cta: 'See the example canvas →',
  href: '/demo/sample-business-model-canvas',
  preview: <BusinessModelCanvasMiniPreview />,
  phase: 'business',
  filter: { question: 'Does the whole business hold?', consequence: 'Before you scale.' },
};

/**
 * The Customer Discovery card — a built feature with a public sample. Added to
 * the climb's "A spark" phase, after the needs discovery memo (the interview
 * guide is built FROM that memo). Kept out of DELIVERABLES so the students
 * gallery stays the curated six.
 */
export const CUSTOMER_DISCOVERY_CARD: DeliverableData = {
  tag: 'Field Kit',
  title: 'A customer discovery kit',
  description:
    'A Mom Test interview guide and survey deck, grounded in your memo, so your first customer conversations probe real assumptions, not hypotheticals.',
  cta: 'See the example kit →',
  href: '/demo/sample-customer-discovery',
  preview: <CustomerDiscoveryMiniPreview />,
  phase: 'spark',
  filter: { question: 'Will customers pay for this?', consequence: 'Before you build.' },
};

/**
 * The card set for the landing-page climb (TheClimb): the six deliverables plus
 * the customer discovery kit (A spark phase) and the business model canvas
 * (business phase). The students gallery keeps the curated six (DELIVERABLES);
 * all share the same DeliverableCard + preview source. Array order sets the
 * per-phase card order (the climb filters by `phase`, preserving order).
 */
export const CLIMB_DELIVERABLES: DeliverableData[] = [
  DELIVERABLES[0], // needs discovery memo (spark)
  CUSTOMER_DISCOVERY_CARD, // customer discovery kit (spark) — after needs discovery
  DELIVERABLES[1], // invention strategy memo (invention)
  DELIVERABLES[3], // prior-art search plan (invention)
  DELIVERABLES[4], // buildable design breakdown (product)
  DELIVERABLES[2], // business case memo (business)
  BUSINESS_MODEL_CANVAS_CARD, // business model canvas (business)
  DELIVERABLES[5], // vetted attorney match (business)
];

/**
 * Deliverables gallery — "Your advisory board" proof section.
 *
 * Renders a brass section eyebrow, a big serif H2, supporting copy, and
 * five deliverable cards (needs discovery memo, invention strategy memo,
 * prior-art search plan, attorney match, business case) — each with a
 * SPECIMEN-style mini-preview of the real artifact and a CTA linking to the
 * public sample.
 *
 * Used on both the main landing page (Landing.tsx) and the students landing
 * page (Students.tsx). The chatbot perception flagged in user testing applies
 * to both audiences, so this gallery is the canonical "we deliver real
 * things, not ChatGPT conversation" proof.
 *
 * PREVIEW PHILOSOPHY (revised per design review): the panes are NOT shrunken
 * documents. Rendering a full artifact at ~375px wide forced 6-8px text that
 * read as blurry noise and made the section look broken. Instead each pane is
 * a SPECIMEN — 2-3 large, legible, branded signal elements (a verdict pill, a
 * few chips, a headline figure) in a framed sage-cream window. Hard rule:
 * nothing in a preview pane below 11px. The mini-preview components stay
 * private to this file; if a real deliverable page changes its recognizable
 * shape, update the matching specimen here to keep the proof honest.
 */
export function DeliverablesGallery() {
  return (
    <section
      style={{
        padding: '96px 32px 128px',
        background: 'var(--surface-page)',
      }}
    >
      <div style={{ maxWidth: 1180, margin: '0 auto' }}>
        {/* Section eyebrow — leads with the ownable "advisory board" framing
            (the nav/aria label), promoted to a prominent brass mono treatment
            with the brand's brass underline tick. */}
        <div
          style={{
            fontFamily: 'var(--f-mono)',
            fontSize: 16,
            fontWeight: 700,
            letterSpacing: '0.18em',
            textTransform: 'uppercase',
            color: 'var(--brass-deep)',
            textAlign: 'center',
            marginBottom: 22,
            paddingBottom: 14,
          }}
        >
          <span style={{ position: 'relative', display: 'inline-block' }}>
            Your personal advisory board
            <span
              style={{
                position: 'absolute',
                bottom: -10,
                left: '50%',
                transform: 'translateX(-50%)',
                width: 48,
                height: 2,
                background: 'var(--brass)',
              }}
            />
          </span>
        </div>
        <h2
          className="f-display"
          style={{
            fontSize: 'clamp(32px, 5vw, 52px)',
            fontWeight: 400,
            lineHeight: 1.1,
            textAlign: 'center',
            marginBottom: 16,
            maxWidth: 960,
            marginLeft: 'auto',
            marginRight: 'auto',
          }}
        >
          Six structured deliverables.
          <br />
          Not six AI chat responses.
        </h2>
        <p
          style={{
            fontSize: 17,
            lineHeight: 1.6,
            color: 'var(--text-decorative)',
            textAlign: 'center',
            maxWidth: 720,
            margin: '0 auto 18px',
          }}
        >
          A product strategist, IP consultant, and financial analyst
          — each produces a real artifact. Plus a prior-art search
          plan, a buildable design breakdown, and a curated attorney
          match. Five live today, one on the way.
        </p>
        {/* Click-instruction — centered and width-constrained so it reads as
            an action prompt under the subhead, not a full-width floating bar. */}
        <p
          style={{
            fontSize: 12,
            fontFamily: 'var(--f-mono)',
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: 'var(--brass-deep)',
            fontWeight: 700,
            textAlign: 'center',
            maxWidth: 640,
            margin: '0 auto 56px',
          }}
        >
          Click any card below to view the complete example →
        </p>

        {/* Six cards in a clean 3+3 grid. Row 1 is the three advisory-board
            specialists in funnel order (Product Strategist → IP Consultant →
            Financial Analyst); row 2 is the operational deliverables (search,
            design, attorney match):
            Needs Discovery → Strategy Memo → Business Case →
            Search Plan → Design Workspace → Marketplace. */}
        <div
          className="deliverables-gallery"
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: 24,
            alignItems: 'stretch',
          }}
        >
          {DELIVERABLES.map((d) => (
            <DeliverableCard key={d.title} {...d} />
          ))}
        </div>

        <p
          style={{
            marginTop: 48,
            fontSize: 14,
            color: 'var(--text-decorative)',
            textAlign: 'center',
            fontStyle: 'italic',
            maxWidth: 640,
            marginLeft: 'auto',
            marginRight: 'auto',
            lineHeight: 1.6,
          }}
        >
          Every artifact above came from a real invention example we
          evaluated. Not screenshots, not mockups — the actual product
          output, scaled down.
        </p>
      </div>

      <style>{`
        .deliverable-card { cursor: pointer; }
        .deliverable-card:hover {
          border-color: rgba(181,133,60,0.55) !important;
          box-shadow: 0 1px 2px rgba(15,23,20,.04), 0 12px 28px rgba(15,23,20,.08) !important;
          transform: translateY(-2px);
          transition: border-color 160ms ease, box-shadow 160ms ease, transform 160ms ease;
        }
        /* CTA fills HEARTWOOD on hover — resting state is brass-as-accent,
           active state is heartwood-as-action, consistent with every primary
           button on the site. */
        .deliverable-card:hover .deliverable-cta {
          background-color: var(--heartwood) !important;
          border-color: var(--heartwood) !important;
          color: var(--surface-page) !important;
        }
        @media (max-width: 980px) {
          .deliverables-gallery {
            grid-template-columns: repeat(2, 1fr) !important;
            max-width: 720px;
            margin-left: auto !important;
            margin-right: auto !important;
          }
        }
        @media (max-width: 619px) {
          .deliverables-gallery {
            grid-template-columns: 1fr !important;
            max-width: 460px;
          }
        }
      `}</style>
    </section>
  );
}

/* -------------------------------------------------------------- */
/* Card                                                           */
/* -------------------------------------------------------------- */

export function DeliverableCard({
  tag,
  title,
  description,
  cta,
  href,
  preview,
  filter,
  comingSoon = false,
  compact = false,
}: {
  tag: string;
  title: string;
  description: string;
  cta: string;
  href: string;
  preview: React.ReactNode;
  /** Accepted (from the spread DeliverableData) but not used here. */
  phase?: DeliverableData['phase'];
  /** Decision-funnel filter line — rendered only in the compact climb card. */
  filter?: DeliverableData['filter'];
  comingSoon?: boolean;
  /** Climb variant: ~half-height card — a short horizontal specimen pane, no
      tag, no description, a slim title, and a mono text-link CTA. */
  compact?: boolean;
}) {
  return (
    <Link
      href={href}
      style={{
        display: 'flex',
        flexDirection: 'column',
        padding: compact ? '16px' : '24px',
        // Coming-soon cards sit a touch flatter (page cream vs card cream) so
        // they read as "not yet" without the broken look of dimming.
        background: comingSoon ? 'var(--surface-page)' : 'var(--surface-card)',
        border: '1px solid var(--hairline-card)',
        borderRadius: compact ? 14 : 16,
        textDecoration: 'none',
        color: 'var(--ink)',
        transition: 'border-color 160ms ease, box-shadow 160ms ease, transform 160ms ease',
      }}
      className="deliverable-card"
    >
      {/* Specimen window — framed sage-cream pane (cooler than the warm card,
          so it separates). Tall 4:3 by default; a short fixed "ticket" in the
          compact climb variant, where the specimen relays out horizontally. */}
      <div
        style={{
          position: 'relative',
          width: '100%',
          ...(compact ? { height: 150 } : { aspectRatio: '4 / 3' }),
          marginBottom: compact ? 14 : 22,
          background: 'color-mix(in srgb, var(--heartwood) 6%, var(--surface-page))',
          border: '1px solid var(--hairline-card)',
          borderRadius: 10,
          overflow: 'hidden',
        }}
      >
        <CompactSpecimenContext.Provider value={compact}>
          {preview}
        </CompactSpecimenContext.Provider>
        {comingSoon && (
          <div
            style={{
              position: 'absolute',
              top: 10,
              right: 10,
              padding: '4px 10px',
              background: 'transparent',
              border: '1px solid var(--brass)',
              color: 'var(--brass-deep)',
              borderRadius: 999,
              fontFamily: 'var(--f-mono)',
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              pointerEvents: 'none',
            }}
          >
            Coming soon
          </div>
        )}
      </div>

      {/* Tag — redundant with the pane label in compact mode, so dropped. */}
      {!compact && (
        <div
          className="f-mono"
          style={{
            fontSize: 11.5,
            letterSpacing: '0.09em',
            textTransform: 'uppercase',
            color: 'var(--brass-deep)',
            fontWeight: 600,
            marginBottom: 8,
          }}
        >
          {tag}
        </div>
      )}

      <h3
        className="f-heading"
        style={
          compact
            ? {
                fontSize: 17,
                fontWeight: 500,
                lineHeight: 1.2,
                margin: 0,
                color: 'var(--ink)',
                // Never let the flex column compress the title (which would clip
                // its descenders via the line-clamp overflow) — the card box is
                // sized to fit title + filter + CTA, but this guards the edge.
                flexShrink: 0,
                // Clamp to 2 lines so a long title can't blow the height budget.
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                // Reserve descender space so a single-line title never clips the
                // 'y' / 'g' / 'p' tails at the box's bottom edge.
                paddingBottom: 2,
              }
            : {
                fontSize: 20,
                fontWeight: 500,
                lineHeight: 1.25,
                marginBottom: 10,
                color: 'var(--ink)',
              }
        }
      >
        {title}
      </h3>

      {/* Decision-funnel filter line (compact climb card only) — a "gate stamp"
          that reads as the test this stage applies to your idea: a mono brass
          question (the filter criterion) over a muted sans consequence (the
          expensive mistake it spares you), marked by a left brass rule that
          rhymes with the spine. Distinct from the serif title and the bordered
          CTA pill. */}
      {compact && filter && (
        <div className="deliverable-filter">
          <span className="deliverable-filter__q">{filter.question}</span>
          {filter.consequence}
        </div>
      )}

      {/* Description — the biggest single height cut; dropped in compact mode
          (the specimen + title already say what the artifact is). */}
      {!compact && (
        <p
          style={{
            fontSize: 14,
            color: 'rgba(15,23,20,0.66)',
            lineHeight: 1.55,
            marginBottom: 18,
            flex: 1,
          }}
        >
          {description}
        </p>
      )}

      <span
        className={`deliverable-cta${compact ? ' deliverable-cta--compact' : ''}`}
        style={
          compact
            ? {
                // Compact: a real brass-outline PILL (not a bare text link) so it
                // unmistakably reads as a clickable button. Brass-tint fill +
                // border; hover fills solid brass (handled in CLIMB_CSS).
                display: 'inline-flex',
                alignItems: 'center',
                alignSelf: 'flex-start',
                gap: 6,
                marginTop: 14,
                padding: '6px 13px',
                fontFamily: 'var(--f-mono)',
                fontSize: 11,
                letterSpacing: '0.04em',
                color: 'var(--brass-deep)',
                fontWeight: 700,
                textTransform: 'uppercase',
                background: 'color-mix(in srgb, var(--brass) 12%, transparent)',
                border: '1px solid color-mix(in srgb, var(--brass) 55%, transparent)',
                borderRadius: 999,
                transition: 'background-color 140ms ease, color 140ms ease, border-color 140ms ease',
              }
            : {
                display: 'inline-flex',
                alignItems: 'center',
                alignSelf: 'flex-start',
                padding: '8px 16px',
                fontFamily: 'var(--f-mono)',
                fontSize: 12,
                letterSpacing: '0.04em',
                color: 'var(--brass-deep)',
                fontWeight: 700,
                textTransform: 'uppercase',
                background: 'transparent',
                border: '1px solid var(--brass)',
                borderRadius: 999,
                transition: 'background-color 140ms ease, color 140ms ease, border-color 140ms ease',
              }
        }
      >
        {cta}
      </span>
    </Link>
  );
}

/* -------------------------------------------------------------- */
/* Specimen building blocks (≥11px everywhere)                    */
/* -------------------------------------------------------------- */

/**
 * True inside a compact climb card. When set, every Specimen relays itself out:
 * the first child (the PaneLabel) stays on top, and the remaining elements (the
 * focal element + its support) flow into one horizontal row — so the specimen
 * fits a short ~150px "ticket" pane instead of a tall 4:3 one. Set by
 * DeliverableCard; the individual specimen components don't change.
 */
const CompactSpecimenContext = createContext(false);

/** Specimen frame. Tall vertical stack by default; compact horizontal
    label-on-top / focal|support row inside a climb card. */
function Specimen({ children }: { children: React.ReactNode }) {
  const compact = useContext(CompactSpecimenContext);

  if (compact) {
    const kids = Children.toArray(children);
    const [label, ...rest] = kids;
    return (
      <div
        style={{
          height: '100%',
          padding: 14,
          display: 'flex',
          flexDirection: 'column',
          gap: 9,
        }}
      >
        {label}
        <div
          style={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            alignItems: 'center',
            gap: 16,
          }}
        >
          {rest}
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        height: '100%',
        padding: 18,
        display: 'flex',
        flexDirection: 'column',
        gap: 14,
        justifyContent: 'center',
      }}
    >
      {children}
    </div>
  );
}

/** A 10.5px brass micro-label above a specimen element. The only sub-11px
 *  type allowed — it's a caps label, not content, so it stays crisp. */
function PaneLabel({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="f-mono"
      style={{
        fontSize: 10.5,
        letterSpacing: '0.12em',
        textTransform: 'uppercase',
        color: 'var(--brass-deep)',
        fontWeight: 700,
      }}
    >
      {children}
    </div>
  );
}

/** The hero verdict pill — the single focal element of a pane. */
function VerdictPill({
  label,
  tone,
}: {
  label: string;
  tone: 'go' | 'pivot';
}) {
  const go = tone === 'go';
  return (
    <div
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        alignSelf: 'flex-start',
        padding: '6px 14px',
        borderRadius: 999,
        background: go ? 'rgba(40,72,52,0.10)' : 'rgba(181,133,60,0.12)',
        color: go ? 'var(--heartwood)' : 'var(--brass-deep)',
      }}
    >
      <span
        style={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          background: go ? 'var(--heartwood)' : 'var(--brass)',
        }}
      />
      <span
        className="f-mono"
        style={{
          fontSize: 14,
          fontWeight: 700,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
        }}
      >
        {label}
      </span>
    </div>
  );
}

/** A small 12px mono chip. */
function Chip({
  children,
  active = false,
}: {
  children: React.ReactNode;
  active?: boolean;
}) {
  return (
    <span
      className="f-mono"
      style={{
        fontSize: 12,
        fontWeight: 600,
        letterSpacing: '0.04em',
        padding: '3px 9px',
        borderRadius: 6,
        color: active ? 'var(--parchment)' : 'var(--brass-deep)',
        background: active ? 'var(--brass)' : 'rgba(181,133,60,0.10)',
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </span>
  );
}

/* -------------------------------------------------------------- */
/* Specimen previews                                              */
/* -------------------------------------------------------------- */

/** Needs Discovery — big "Promising" verdict + a 3-bar market-size chart. */
function NeedsDiscoveryMiniPreview() {
  const bars = [
    { label: 'TAM', h: 100 },
    { label: 'SAM', h: 58 },
    { label: 'SOM', h: 24 },
  ];
  return (
    <Specimen>
      <PaneLabel>Needs discovery</PaneLabel>
      <VerdictPill label="Promising" tone="go" />
      <div>
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-end',
            gap: 12,
            height: 56,
            marginBottom: 6,
          }}
        >
          {bars.map((b) => (
            <div
              key={b.label}
              style={{
                width: 26,
                height: `${b.h}%`,
                borderRadius: '4px 4px 0 0',
                background: 'var(--heartwood)',
                opacity: 0.35 + (b.h / 100) * 0.55,
              }}
            />
          ))}
        </div>
        <div style={{ display: 'flex', gap: 12 }}>
          {bars.map((b) => (
            <span
              key={b.label}
              className="f-mono"
              style={{
                width: 26,
                fontSize: 11,
                fontWeight: 700,
                color: 'var(--text-decorative)',
                textAlign: 'center',
              }}
            >
              {b.label}
            </span>
          ))}
        </div>
      </div>
    </Specimen>
  );
}

/** Invention Strategy — big "Pivot" verdict + GO · PIVOT · STOP chips. */
function MemoMiniPreview() {
  return (
    <Specimen>
      <PaneLabel>Invention strategy</PaneLabel>
      <VerdictPill label="Pivot" tone="pivot" />
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        <Chip>Go</Chip>
        <Chip active>Pivot</Chip>
        <Chip>Stop</Chip>
      </div>
      <div
        style={{
          fontFamily: 'var(--f-serif)',
          fontStyle: 'italic',
          fontSize: 12.5,
          color: 'var(--text-primary)',
          lineHeight: 1.4,
        }}
      >
        3 prior-art citations · provisional path
      </div>
    </Specimen>
  );
}

/** Prior-art Search — 3 keyword chips + one truncated Boolean line. */
function SearchStrategyMiniPreview() {
  return (
    <Specimen>
      <PaneLabel>Prior-art search</PaneLabel>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        <Chip>wrist rest</Chip>
        <Chip>air bladder</Chip>
        <Chip>USB-C</Chip>
      </div>
      {/* minWidth:0 lets this shrink as a flex item in the compact horizontal
          row, so the Boolean line ellipsizes inside the pane instead of
          overflowing it. Harmless in the default vertical layout. */}
      <div style={{ minWidth: 0 }}>
        <div
          className="f-mono"
          style={{
            fontSize: 11,
            letterSpacing: '0.06em',
            color: 'var(--brass-deep)',
            fontWeight: 700,
            marginBottom: 6,
          }}
        >
          A47B 85/00 · H05B 3/34
        </div>
        <code
          style={{
            display: 'block',
            fontFamily: 'var(--f-mono)',
            fontSize: 11,
            color: 'var(--text-primary)',
            background: 'var(--surface-card)',
            border: '1px solid var(--hairline-card)',
            borderRadius: 6,
            padding: '6px 8px',
            lineHeight: 1.4,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          (&quot;wrist rest&quot; OR &quot;wrist support&quot;) AND…
        </code>
      </div>
    </Specimen>
  );
}

/** Marketplace — one legible attorney mini-row. */
function MarketplaceMiniPreview() {
  return (
    <Specimen>
      <PaneLabel>Attorney match</PaneLabel>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div
          style={{
            width: 36,
            height: 36,
            borderRadius: '50%',
            background: 'var(--heartwood)',
            color: 'var(--parchment)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontFamily: 'var(--f-serif)',
            fontSize: 14,
            fontWeight: 600,
            flexShrink: 0,
          }}
        >
          MM
        </div>
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              fontFamily: 'var(--f-serif)',
              fontWeight: 500,
              fontSize: 14,
              color: 'var(--ink)',
              lineHeight: 1.2,
            }}
          >
            Patent attorney · Solo
          </div>
          <div
            className="f-mono"
            style={{
              fontSize: 11,
              color: 'var(--text-decorative)',
              marginTop: 2,
            }}
          >
            USPTO reg. · Minneapolis, MN
          </div>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        <Chip>Mechanical</Chip>
        <Chip>Accepts micro-entity</Chip>
      </div>
    </Specimen>
  );
}

/** Design Workspace — two numbered component rows with material chips,
 *  signalling "idea broken into labeled parts with materials." */
function DesignMiniPreview() {
  const parts = [
    { n: 2, name: 'Air bladder', mat: 'Welded TPU' },
    { n: 4, name: 'Heating film', mat: 'Polyimide' },
  ];
  return (
    <Specimen>
      <PaneLabel>Design decomposition</PaneLabel>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {parts.map((p) => (
          <div
            key={p.n}
            style={{ display: 'flex', alignItems: 'center', gap: 10 }}
          >
            <span
              className="f-mono"
              style={{
                flexShrink: 0,
                width: 22,
                height: 22,
                borderRadius: '50%',
                background: 'var(--heartwood)',
                color: 'var(--parchment)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 11,
                fontWeight: 700,
              }}
            >
              {p.n}
            </span>
            <span
              style={{
                fontFamily: 'var(--f-serif)',
                fontSize: 13.5,
                fontWeight: 500,
                color: 'var(--ink)',
              }}
            >
              {p.name}
            </span>
            <Chip>{p.mat}</Chip>
          </div>
        ))}
      </div>
      <div
        className="f-mono"
        style={{
          fontSize: 11,
          letterSpacing: '0.06em',
          color: 'var(--text-decorative)',
          fontWeight: 700,
        }}
      >
        7 components · 12 materials
      </div>
    </Specimen>
  );
}

/** Business Case — a big 5-yr valuation figure + a 4-bar EBITDA chart. */
function BusinessCaseMiniPreview() {
  const bars = [22, 14, 46, 78];
  return (
    <Specimen>
      <PaneLabel>5-yr business case</PaneLabel>
      <div
        style={{
          fontFamily: 'var(--f-serif)',
          fontSize: 24,
          fontWeight: 500,
          color: 'var(--brass-deep)',
          lineHeight: 1,
        }}
      >
        $18M&ndash;$42M
      </div>
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          gap: 8,
          height: 44,
        }}
      >
        {bars.map((h, i) => (
          <div
            key={i}
            style={{
              width: 22,
              height: `${h}%`,
              borderRadius: '3px 3px 0 0',
              background: 'var(--heartwood)',
              opacity: 0.3 + (h / 100) * 0.6,
            }}
          />
        ))}
      </div>
      <div
        className="f-mono"
        style={{
          fontSize: 11,
          letterSpacing: '0.06em',
          color: 'var(--text-decorative)',
          fontWeight: 700,
        }}
      >
        EBITDA · breakeven Y3
      </div>
    </Specimen>
  );
}

/** Business Model Canvas — a mini 9-block grid, the value-props block washed
 *  heartwood and labeled. The grid structure itself signals "canvas"; only the
 *  center carries text (11px), the rest are content lines, so nothing dips
 *  below the legibility floor. */
function BusinessModelCanvasMiniPreview() {
  return (
    <Specimen>
      <PaneLabel>Business model canvas</PaneLabel>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: 5,
        }}
      >
        {Array.from({ length: 9 }).map((_, i) => {
          const isCenter = i === 4;
          return (
            <div
              key={i}
              style={{
                height: 32,
                borderRadius: 4,
                border: '1px solid color-mix(in srgb, var(--ink) 14%, transparent)',
                background: isCenter
                  ? 'color-mix(in srgb, var(--heartwood) 12%, transparent)'
                  : 'transparent',
                display: 'flex',
                flexDirection: 'column',
                alignItems: isCenter ? 'center' : 'flex-start',
                justifyContent: 'center',
                gap: 3,
                padding: '0 6px',
              }}
            >
              {isCenter ? (
                <span
                  style={{
                    fontFamily: 'var(--f-sans)',
                    fontSize: 11,
                    fontWeight: 700,
                    color: 'var(--heartwood)',
                  }}
                >
                  Value
                </span>
              ) : (
                <>
                  <span
                    style={{
                      width: '70%',
                      height: 2,
                      borderRadius: 1,
                      background: 'color-mix(in srgb, var(--ink) 18%, transparent)',
                    }}
                  />
                  <span
                    style={{
                      width: '45%',
                      height: 2,
                      borderRadius: 1,
                      background: 'color-mix(in srgb, var(--ink) 13%, transparent)',
                    }}
                  />
                </>
              )}
            </div>
          );
        })}
      </div>
      <div
        className="f-mono"
        style={{
          fontSize: 11,
          letterSpacing: '0.06em',
          color: 'var(--text-decorative)',
          fontWeight: 700,
        }}
      >
        9 blocks · one page
      </div>
    </Specimen>
  );
}

/** Customer Discovery — a mini interview-guide: a section label + three
 *  question lines with spark-green tick dots, in the field-kit posture. */
function CustomerDiscoveryMiniPreview() {
  const lines = ['85%', '70%', '92%'];
  return (
    <Specimen>
      <PaneLabel>Interview guide</PaneLabel>
      <div
        className="f-serif"
        style={{ fontSize: 14, color: 'var(--ink)', fontWeight: 500, marginBottom: 4 }}
      >
        <span className="f-mono" style={{ fontSize: 11, color: 'var(--text-decorative)', marginRight: 8 }}>
          02
        </span>
        Pain probe
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
        {lines.map((w, i) => (
          <div key={i} style={{ display: 'flex', gap: 9, alignItems: 'center' }}>
            <span
              style={{
                flex: 'none',
                width: 6,
                height: 6,
                borderRadius: '50%',
                background: 'var(--spark)',
              }}
            />
            <span
              style={{
                height: 8,
                width: w,
                borderRadius: 2,
                background: 'color-mix(in srgb, var(--ink) 16%, transparent)',
              }}
            />
          </div>
        ))}
      </div>
      <div
        className="f-mono"
        style={{
          fontSize: 11,
          letterSpacing: '0.06em',
          color: 'var(--text-decorative)',
          fontWeight: 700,
          marginTop: 4,
        }}
      >
        Mom Test · grounded in your memo
      </div>
    </Specimen>
  );
}
